// helpers/sentryHelpers.js
import * as Sentry from "@sentry/react";

const isProduction = process.env.REACT_APP_SENTRY === "true";

export const captureError = (err, context) => Sentry.captureException(err, { extra: context });

export const countMetric = (metricName, value = 1) => {
  if (!isProduction) return;
  Sentry.metrics?.count?.(metricName, value);
};

/** Capture API error with tags and extra. No-op when Sentry is disabled. */
export const captureApiException = (err, options = {}) => {
  if (!isProduction) return;
  const { level = "error", tags = {}, extra = {} } = options;
  Sentry.captureException(err, { level, tags, extra });
};

/** Capture API success event (info). No-op when Sentry is disabled. */
export const captureApiEvent = (event) => {
  if (!isProduction) return;
  Sentry.captureEvent({
    level: "info",
    ...event,
  });
};

export const setSentryExtras = () => {
  Sentry.setExtras({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: "web",
  });
};

export const setSentryUser = (user = null) => {
  // Only execute if Sentry is initialized (production environment)
  if (!isProduction || !user) {
    return;
  }
  Sentry.setUser(user);
  Sentry.setTag("role", user?.role);
  Sentry.setTag("hub_name", user?.hubName);
  Sentry.setTag("hub_id", user?.hubId);
  Sentry.captureMessage("Initial event to register tags");
};
// export const setSentryTags = () => {
//   Sentry.setTags({
//     app_version: pkg.version,
//     app_name: pkg.name,
//     env: process.env.NODE_ENV,
//   });
// };

/**
 * Capture API errors with endpoint + payload context
 */
export const captureApiError = (error, { url, method, payload } = {}) => {
  Sentry.withScope((scope) => {
    scope.setTag("error_type", "api_error");
    scope.setContext("api_details", {
      url,
      method,
      payload,
    });
    Sentry.captureException(error);
  });
};

/**
 * Capture form validation issues
 */
export const captureValidationError = (message, formName, fieldName) => {
  Sentry.withScope((scope) => {
    scope.setTag("error_type", "validation_error");
    scope.setContext("validation_details", {
      formName,
      fieldName,
    });
    Sentry.captureMessage(message, "warning");
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (featureName) => {
  Sentry.captureMessage(`Feature used: ${featureName}`, {
    level: "info",
  });
};

/**
 * Capture info-level message (e.g. success events) with optional context
 */
export const captureInfo = (message, extra = {}) => {
  Sentry.captureMessage(message, {
    level: "info",
    extra,
  });
};

/**
 * Add manual breadcrumb
 */
export const addBreadcrumb = (message, category = "custom") => {
  Sentry.addBreadcrumb({
    message,
    category,
    level: "info",
  });
};

/**
 * Attach global promise rejection listener
 */
export const attachGlobalErrorHandlers = () => {
  window.addEventListener("unhandledrejection", (event) => {
    Sentry.captureException(event.reason);
  });
};

/**
 * Set global app metadata
 */
export const setAppMetadata = (version, appName) => {
  Sentry.setTags({
    app_version: version,
    app_name: appName,
    env: process.env.NODE_ENV,
  });
};

/**
 * Track route change
 */
export const trackRouteChange = (routeName) => {
  Sentry.addBreadcrumb({
    category: "navigation",
    message: `Navigated to ${routeName}`,
    level: "info",
  });
};

/**
 * Clear user and tags on logout
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTags({});
};

/**
 * Capture non-critical warnings
 */
export const captureWarning = (message, context = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setExtras(context);
    Sentry.captureMessage(message);
  });
};

/**
 * Start a performance span (modern Sentry v7+)
 */
export const startAppSpan = ({
  name,
  op = "ui.action",
  attributes = {},
  forceTransaction = false,
  callback,
}) => {
  return Sentry.startSpan({ name, op, attributes, forceTransaction: forceTransaction }, callback);
};

export const startManualSpan = (name, op = "custom") => {
  return Sentry.startInactiveSpan({ name, op });
};
export const logToSentry = (message, data = {}, level = "info") => {
  if (Sentry?.logger?.[level]) {
    Sentry.logger[level](message, data);
  } else {
    Sentry.logger.info(message, data);
  }
};
/** Default export for usage as sentry.captureInfo(), sentry.captureError(), etc. */
export default {
  captureError,
  captureInfo,
  captureApiError,
  captureValidationError,
  captureWarning,
  trackFeatureUsage,
  addBreadcrumb,
  countMetric,
  setSentryExtras,
  setSentryUser,
  clearSentryUser,
  trackRouteChange,
  setAppMetadata,
  attachGlobalErrorHandlers,
  startAppSpan,
  startManualSpan,
  logToSentry,
};
